// src/admin/TeamManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where, getDoc
} from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../firebase';
import {
  UserPlus, Edit, Trash2, Key, X, Search, Mail, Phone, Shield, Ghost
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const firebaseConfig = {
  apiKey: "AIzaSyASOCPRbKYy3oQBscJEYNCHp5jC0kSTV0Q",
  authDomain: "organic-store-5d667.firebaseapp.com",
  projectId: "organic-store-5d667",
  storageBucket: "organic-store-5d667.firebasestorage.app",
  messagingSenderId: "951551672704",
  appId: "1:951551672704:web:0fce549e67798afe26c165"
};

const TEAM_ROLES = ['superghost', 'superadmin', 'admin', 'manager', 'employee'];

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', idCard: '', password: '',
    role: 'employee',
  });

  // ================= FETCH TEAM =================
  const fetchTeam = async () => {
    setLoading(true);
    try {
      // 1. Get current logged-in user
      const loggedInUser = auth.currentUser;
      if (!loggedInUser) {
        setLoading(false);
        return;
      }

      setCurrentUserId(loggedInUser.uid);

      // 2. Get current user's role from Firestore
      const currentUserDoc = await getDoc(doc(db, 'users', loggedInUser.uid));
      const currentRole = currentUserDoc.exists() ? currentUserDoc.data().role : '';
      setCurrentUserRole(currentRole);

      // 3. Fetch all team members
      const q = query(collection(db, 'users'), where('role', 'in', TEAM_ROLES));
      const snap = await getDocs(q);
      let members = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // 4. Filter out Super Ghosts (unless current user is Super Ghost themselves)
      if (currentRole !== 'superghost') {
        // Non-superghost users → Super Ghosts invisible
        members = members.filter(m => m.role !== 'superghost');
      } else {
        // Current user is Super Ghost → only show SELF, hide other Super Ghosts
        members = members.filter(m =>
          m.role !== 'superghost' || m.id === loggedInUser.uid
        );
      }

      setTeamMembers(members);
    } catch (error) {
      console.error('Error fetching team:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const openAddModal = () => {
    setEditingMember(null);
    setFormData({ name: '', email: '', phone: '', idCard: '', password: '', role: 'employee' });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      email: member.email || '',
      phone: member.phone || '',
      idCard: member.idCard || '',
      password: '',
      role: member.role || 'employee',
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  // ================= CREATE USER =================
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setMessage({ type: 'error', text: 'All fields are required.' });
      return;
    }
    if (formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }

    // ✅ Only Super Ghost can create another Super Ghost
    if (formData.role === 'superghost' && currentUserRole !== 'superghost') {
      setMessage({ type: 'error', text: 'Only Super Ghost Admin can create another Super Ghost Admin.' });
      return;
    }

    try {
      const secondaryApp = initializeApp(firebaseConfig, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, formData.email, formData.password
      );
      const newUserId = userCredential.user.uid;

      await secondaryAuth.signOut();
      await deleteApp(secondaryApp);

      await setDoc(doc(db, 'users', newUserId), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        idCard: formData.idCard || '',
        role: formData.role,
        createdAt: new Date(),
      });

      setShowModal(false);
      setSuccessMessage(`${formData.name} successfully added as ${formData.role}!`);
      setShowSuccess(true);
      fetchTeam();
    } catch (error) {
      console.error('Error creating user:', error);
      if (error.code === 'auth/email-already-in-use') {
        setMessage({ type: 'error', text: 'This email is already registered.' });
      } else if (error.code === 'auth/invalid-email') {
        setMessage({ type: 'error', text: 'Invalid email address.' });
      } else {
        setMessage({ type: 'error', text: 'Failed to create user.' });
      }
    }
  };

  // ================= UPDATE USER =================
  const handleUpdateUser = async (e) => {
    e.preventDefault();

    // ✅ Only Super Ghost can change role to/from Super Ghost
    if (
      (editingMember.role === 'superghost' || formData.role === 'superghost')
      && currentUserRole !== 'superghost'
    ) {
      setMessage({ type: 'error', text: 'Only Super Ghost Admin can edit Super Ghost role.' });
      return;
    }

    try {
      await updateDoc(doc(db, 'users', editingMember.id), {
        name: formData.name,
        phone: formData.phone,
        idCard: formData.idCard || '',
        role: formData.role,
      });
      setShowModal(false);
      setSuccessMessage('Team member updated successfully!');
      setShowSuccess(true);
      fetchTeam();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update user.' });
    }
  };

  // ================= DELETE USER =================
  const handleDelete = async (member) => {
    // ✅ Super Ghost can NEVER be deleted
    if (member.role === 'superghost') {
      alert('Super Ghost Admin cannot be deleted!');
      return;
    }

    // ✅ Only Super Ghost can delete Super Admin
    if (member.role === 'superadmin' && currentUserRole !== 'superghost') {
      alert('Only Super Ghost Admin can delete Super Admin.');
      return;
    }

    if (!window.confirm(`Delete ${member.name}?`)) return;

    try {
      await deleteDoc(doc(db, 'users', member.id));
      setSuccessMessage('Team member deleted!');
      setShowSuccess(true);
      fetchTeam();
    } catch (error) {
      alert('Failed to delete user.');
    }
  };

  const handlePasswordReset = async (member) => {
    if (!window.confirm(`Send password reset email to ${member.email}?`)) return;
    try {
      await sendPasswordResetEmail(getAuth(), member.email);
      setSuccessMessage(`Password reset email sent to ${member.email}`);
      setShowSuccess(true);
    } catch (error) {
      alert('Failed to send reset email.');
    }
  };

  const filteredTeam = teamMembers.filter(m =>
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const styles = {
      superghost: 'bg-purple-900 text-white',
      superadmin: 'bg-purple-100 text-purple-800',
      admin: 'bg-red-100 text-red-800',
      manager: 'bg-blue-100 text-blue-800',
      employee: 'bg-gray-100 text-gray-800',
    };
    const labels = {
      superghost: '👻 Super Ghost',
      superadmin: 'Super Admin',
      admin: 'Admin',
      manager: 'Manager',
      employee: 'Employee',
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${styles[role] || styles.employee}`}>
        {labels[role] || role}
      </span>
    );
  };

  return (
    <div className="space-y-6">

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Team Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            {currentUserRole === 'superghost'
              ? '👻 Aap Super Ghost Admin hain — baaki Super Ghosts aapko nahi dikhte.'
              : 'Add admin, manager, aur employee accounts.'}
          </p>
        </div>
        <button onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md">
          <UserPlus size={18} /> Add Member
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="relative">
          <input type="text" placeholder="Search by name or email..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm" />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
            <p className="mt-4 text-gray-500">Loading team...</p>
          </div>
        ) : filteredTeam.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Shield size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No team members found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Phone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Role</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTeam.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                          member.role === 'superghost' ? 'bg-purple-900' : 'bg-green-700'
                        }`}>
                          {member.role === 'superghost' ? <Ghost size={16} /> : member.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-medium text-gray-800">{member.name}</span>
                          {member.id === currentUserId && (
                            <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-bold">
                              YOU
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Mail size={14} className="text-gray-400" /> {member.email}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Phone size={14} className="text-gray-400" /> {member.phone || '-'}
                      </div>
                    </td>
                    <td className="px-5 py-4">{getRoleBadge(member.role)}</td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        {/* Only Super Ghost or the user themselves can edit a Super Ghost */}
                        {(member.role !== 'superghost' || member.id === currentUserId || currentUserRole === 'superghost') && (
                          <button onClick={() => openEditModal(member)}
                            className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg transition" title="Edit">
                            <Edit size={16} />
                          </button>
                        )}
                        <button onClick={() => handlePasswordReset(member)}
                          className="text-yellow-600 hover:bg-yellow-50 p-2 rounded-lg transition" title="Send Reset">
                          <Key size={16} />
                        </button>
                        {member.role !== 'superghost' && member.id !== currentUserId && (
                          <button onClick={() => handleDelete(member)}
                            className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">
                {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={editingMember ? handleUpdateUser : handleCreateUser} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                <input type="text" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Ahmed Khan" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                <input type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editingMember}
                  className={`w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm ${
                    editingMember ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="e.g., ahmed@example.com" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                <input type="tel" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="+92 300 1234567" required />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">ID Card Number</label>
                <input type="text" value={formData.idCard}
                  onChange={(e) => setFormData({ ...formData, idCard: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., 35202-1234567-1" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Role *</label>
                <select value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm cursor-pointer bg-white">
                  <option value="employee">Employee (Orders only)</option>
                  <option value="manager">Manager (Products, Orders, Customers)</option>
                  <option value="admin">Admin (Full access except Team)</option>
                  <option value="superadmin">Super Admin (Full access)</option>
                  {currentUserRole === 'superghost' && (
                    <option value="superghost">👻 Super Ghost Admin (Invisible)</option>
                  )}
                </select>

                {formData.role === 'superghost' && (
                  <div className="text-xs text-purple-700 mt-2 bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <strong>⚠️ Super Ghost Admin:</strong>
                    <ul className="list-disc ml-4 mt-1 space-y-0.5">
                      <li>Kisi bhi list mein nahi dikhega (na Customer, na Team mein except khud)</li>
                      <li>Sirf khud apne aap ko dekh sakta hai</li>
                      <li>Delete nahi ho sakta</li>
                      <li>Doosre Super Ghosts ko bhi nahi dikhega</li>
                    </ul>
                  </div>
                )}
              </div>

              {!editingMember && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password *</label>
                  <input type="text" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                    placeholder="Minimum 6 characters" required minLength={6} />
                </div>
              )}

              {editingMember && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                  Password change karne ke liye "Send Password Reset Email" button use karein.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition">
                  {editingMember ? 'Update Member' : 'Create Member'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Success!"
        message={successMessage}
        buttonText="Great!"
      />

    </div>
  );
};

export default TeamManagement;